import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CloseIcon } from '@chakra-ui/icons';
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  IconButton,
  Stack,
  Text,
} from '@chakra-ui/react';
import { SystemTranslationKeys } from '@common/entities';
import { markNotificationAsRead, NotificationPayload } from 'state/slices/notificationsSlice';
import { RootState } from 'state/store';

import { BellIcon } from 'components/custom-icons';
import { useNotificationDrawer } from 'hooks/useNotificationDrawer';
import { useSystemTranslation } from 'hooks/useSystemTranslation';
import { markNotificationAsRead as markNotificationAsReadAPI } from 'services';
import handleError from 'utils/handleError';

import NotificationCard from './NotificationCard';

// Helper function to sort notifications by date descending (most recent first)
const sortNotificationsByDate = (notifications: NotificationPayload[]) => {
  return notifications.sort((a, b) => {
    const dateA = a.date ? new Date(a.date).getTime() : 0;
    const dateB = b.date ? new Date(b.date).getTime() : 0;
    return dateB - dateA;
  });
};

export default function NotificationDrawer() {
  const { tSystem } = useSystemTranslation();
  const { isOpen, onOpen, onClose } = useNotificationDrawer();
  const queue = useSelector((state: RootState) => state?.notifications?.queue);
  const dispatch = useDispatch();

  const handleMarkAsRead = async (ids: string[]) => {
    try {
      // First, we call the API to mark the notification as read on the server.
      await markNotificationAsReadAPI(ids);

      // Update local state
      dispatch(markNotificationAsRead(ids));
    } catch (error) {
      handleError(
        `Form Viewer - Failed to mark notification as read: ${error}`,
        'Failed to mark notification as read',
      );
      // Still update local state even if API fails to mark as read
      dispatch(markNotificationAsRead(ids));
    }
  };

  const handleClearAllNotifications = async () => {
    const ids = queue.filter((n) => n.persist !== false && !n.read).map((n) => n.id);
    if (ids.length === 0) return;
    await handleMarkAsRead(ids);
  };

  const hasUnreadNotifications = queue.filter((n) => n.persist !== false && !n.read).length > 0;

  return (
    <Box position="relative">
      <IconButton
        onClick={onOpen}
        aria-label="NotificationsDrawer"
        data-testid="notifications-drawer-button"
        bg="none"
        icon={
          <Box position="relative">
            <BellIcon boxSize={6} />
            {queue.filter((n) => !n.read).length > 0 && (
              <Box
                data-testid="unread-indicator"
                position="absolute"
                top="2px"
                right="2px"
                width="10px"
                height="10px"
                bg="red.500"
                borderRadius="full"
                border="2px solid white"
              />
            )}
          </Box>
        }
      />

      <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="sm">
        <DrawerOverlay />
        <DrawerContent
          display="flex"
          flexDirection="column"
          bg="white.200"
          borderTopLeftRadius={{ base: 0, md: '2xl' }}
          borderBottomLeftRadius={{ base: 0, md: '2xl' }}
          overflow="hidden"
        >
          <DrawerHeader>
            <Flex justifyContent="space-between" mb={4}>
              <IconButton
                aria-label={tSystem(SystemTranslationKeys.common.close)}
                bg="none"
                icon={<CloseIcon />}
                onClick={onClose}
              />
            </Flex>
            <Flex alignItems="center" gap={2} ml={2}>
              <BellIcon boxSize={6} />
              <Text fontSize="lg" fontWeight="bold">
                {tSystem(SystemTranslationKeys.common.notifications)}
              </Text>
            </Flex>
          </DrawerHeader>
          <DrawerBody flex="1" p={0} display="flex">
            <Stack p={8} flex="1" overflowY="auto" spacing={4}>
              {queue.filter((n) => n.persist !== false).length === 0 ? (
                <Text>{tSystem(SystemTranslationKeys.common.noNotifications)}</Text>
              ) : (
                <>
                  <Flex justifyContent="space-between">
                    <Text
                      fontSize="md"
                      fontWeight="bold"
                      mb={2}
                      data-testid="notifications-new-heading"
                    >
                      {tSystem(SystemTranslationKeys.common.new)}
                    </Text>
                    {hasUnreadNotifications && (
                      <Text
                        fontSize="sm"
                        color="blue.500"
                        fontWeight="medium"
                        cursor="pointer"
                        onClick={handleClearAllNotifications}
                      >
                        {tSystem(SystemTranslationKeys.common.clearAll)}
                      </Text>
                    )}
                  </Flex>
                  {!hasUnreadNotifications ? (
                    <Text
                      fontSize="xs"
                      ml={2}
                      color="gray.700"
                      data-testid="notifications-all-caught-up"
                    >
                      {tSystem(SystemTranslationKeys.common.allCaughtUp)}
                    </Text>
                  ) : (
                    sortNotificationsByDate(
                      queue.filter((n) => n.persist !== false && !n.read),
                    ).map((notification) => (
                      <NotificationCard
                        key={notification.id}
                        id={notification.id}
                        status={notification.status}
                        title={notification.title}
                        message={notification.message}
                        date={notification.date}
                        compact={false}
                        read={notification.read}
                        handleMarkAsRead={handleMarkAsRead}
                        type={notification.type}
                        metadata={notification.metadata}
                      />
                    ))
                  )}
                  {queue.filter((n) => n.persist !== false && n.read).length > 0 && (
                    <>
                      <Text
                        fontSize="md"
                        fontWeight="bold"
                        mt={4}
                        mb={2}
                        data-testid="notifications-past-heading"
                      >
                        {tSystem(SystemTranslationKeys.common.pastNotifications)}
                      </Text>
                      {sortNotificationsByDate(
                        queue.filter((n) => n.persist !== false && n.read),
                      ).map((notification) => (
                        <NotificationCard
                          key={notification.id}
                          id={notification.id}
                          status={notification.status}
                          title={notification.title}
                          message={notification.message}
                          date={notification.date}
                          compact={false}
                          read={notification.read}
                          type={notification.type}
                          metadata={notification.metadata}
                        />
                      ))}
                    </>
                  )}
                </>
              )}
            </Stack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
}
